import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import Footer from '../../components/feature/Footer';
import CategoryCard from './components/CategoryCard';
import EmptyState from './components/EmptyState';
import ErrorState from '../../components/base/ErrorState';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface Category {
  id: number;
  name: string;
  icon_url?: string;
  description?: string;
  product_count?: number;
}

type SortOption = 'az' | 'za' | 'products_desc' | 'products_asc';

const CategoriesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  // Filter & Sort params from URL
  const searchKeyword = searchParams.get('search') || '';
  const sortBy = (searchParams.get('sort') || 'az') as SortOption;
  const pageSize = parseInt(searchParams.get('pageSize') || '24');
  const currentPage = parseInt(searchParams.get('page') || '1');

  useEffect(() => {
    loadCategories();
  }, [searchKeyword, sortBy, pageSize, currentPage]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query
      let query = supabase
        .from('categories')
        .select('*', { count: 'exact' });

      // Search filter
      if (searchKeyword.trim()) {
        query = query.ilike('name', `%${searchKeyword}%`);
      }

      // Database-level sorting (for A-Z and Z-A)
      if (sortBy === 'az') {
        query = query.order('name', { ascending: true });
      } else if (sortBy === 'za') {
        query = query.order('name', { ascending: false });
      } else {
        // For product count sorting, we'll sort client-side after loading product counts
        query = query.order('name', { ascending: true });
      }

      // Pagination
      const offset = (currentPage - 1) * pageSize;
      query = query.range(offset, offset + pageSize - 1);

      const { data: categoriesData, count, error: categoriesError } = await query;

      if (categoriesError) {
        throw categoriesError;
      }

      if (!categoriesData) {
        setCategories([]);
        setTotalCount(0);
        setLoading(false);
        return;
      }

      setTotalCount(count || 0);

      // Load product counts for each category
      const categoryIds = categoriesData.map(c => c.id);
      let productCounts: Record<number, number> = {};

      if (categoryIds.length > 0) {
        const { data: productsData } = await supabase
          .from('products')
          .select('category_id')
          .eq('status', 'active')
          .in('category_id', categoryIds);

        if (productsData) {
          // Count products per category
          productsData.forEach(p => {
            if (p.category_id) {
              productCounts[p.category_id] = (productCounts[p.category_id] || 0) + 1;
            }
          });
        }
      }

      // Combine categories with product counts
      let enrichedCategories: Category[] = categoriesData.map(c => ({
        ...c,
        product_count: productCounts[c.id] || 0,
      }));

      // Client-side sorting for product count
      if (sortBy === 'products_desc') {
        enrichedCategories.sort((a, b) => (b.product_count || 0) - (a.product_count || 0));
      } else if (sortBy === 'products_asc') {
        enrichedCategories.sort((a, b) => (a.product_count || 0) - (b.product_count || 0));
      }

      setCategories(enrichedCategories);
      setLoading(false);
    } catch (err) {
      console.error('Error loading categories:', err);
      setError('Failed to load categories');
      setLoading(false);
    }
  };

  const updateFilter = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    // Reset to page 1 when filters change
    if (key !== 'page') {
      newParams.set('page', '1');
    }
    setSearchParams(newParams);
  };

  const clearSearch = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('search');
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const hasSearch = searchKeyword.trim() !== '';

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <TopNavigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-gray-400 animate-spin"></i>
            <p className="mt-4 text-gray-600">Loading categories...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <TopNavigation />
        <ErrorState onRetry={loadCategories} />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Categories</h1>
          <p className="text-gray-600">Explore product categories and find what you need.</p>
        </div>

        {/* Search & Sort Toolbar */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  placeholder="Search categories"
                  value={searchKeyword}
                  onChange={(e) => updateFilter('search', e.target.value)}
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {hasSearch && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                    title="Clear search"
                  >
                    <i className="ri-close-line text-lg w-5 h-5 flex items-center justify-center"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Sort & Results */}
            <div className="flex items-center gap-3">
              <div className="text-sm text-gray-600">
                {loading ? (
                  <span>Loading...</span>
                ) : (
                  <span className="font-medium">
                    {totalCount} categor{totalCount !== 1 ? 'ies' : 'y'} found
                  </span>
                )}
              </div>

              <select
                value={sortBy}
                onChange={(e) => updateFilter('sort', e.target.value)}
                className="px-4 py-2 pr-8 border border-gray-300 rounded-lg text-sm bg-white cursor-pointer focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="az">A–Z</option>
                <option value="za">Z–A</option>
                <option value="products_desc">Most Products</option>
                <option value="products_asc">Least Products</option>
              </select>
            </div>
          </div>
        </div>

        {/* Main Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin mb-4"></i>
              <p className="text-gray-600">Loading categories...</p>
            </div>
          </div>
        ) : error ? (
          <EmptyState type="error" onRetry={loadCategories} />
        ) : categories.length === 0 ? (
          <EmptyState
            type={hasSearch ? 'no-results' : 'no-categories'}
            onClearSearch={hasSearch ? clearSearch : undefined}
          />
        ) : (
          <>
            {/* Categories Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {categories.map((category) => (
                <CategoryCard key={category.id} category={category} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-4">
                <button
                  onClick={() => updateFilter('page', String(currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Previous
                </button>

                <span className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  onClick={() => updateFilter('page', String(currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  Next
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default CategoriesPage;
